/**
 * Client Users Service
 * Shared fetchers for the assignable-user lists used by the sessions,
 * training, and prompt-library share/assign flows. By default both
 * fetchers are fail-soft: any auth/network/HTTP error yields an empty
 * list. Callers that need to surface load failures (share dialogs) can
 * opt out via `throwOnError`.
 */

import { env } from '@/lib/env'
import { getAccessToken } from '@/services/auth-service'

export interface ClientUser {
  id: string
  name?: string
  email?: string
  client_id?: string
  client_name?: string
}

interface FetchClientUsersOptions {
  /**
   * Filter to users with this app in allowed_applications. Defaults to
   * 'webos'; pass null to list all client users regardless of app
   * (prompt-library share).
   */
  application?: string | null
  /** Admins only: list active users across all clients. */
  allClients?: boolean
  /**
   * Opt out of the fail-soft default: network/parse errors are rethrown,
   * and a missing token or non-OK response yields `null` so callers can
   * keep previously loaded state and show their own error UI.
   */
  throwOnError?: boolean
}

async function fetchUsers(url: string, throwOnError = false): Promise<ClientUser[] | null> {
  const token = getAccessToken()
  if (!token) return throwOnError ? null : []

  try {
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    if (!response.ok) return throwOnError ? null : []
    const data = await response.json()
    return (data.users || []) as ClientUser[]
  } catch (error) {
    if (throwOnError) throw error
    return []
  }
}

export function fetchClientUsers(
  options?: FetchClientUsersOptions & { throwOnError?: false }
): Promise<ClientUser[]>
export function fetchClientUsers(
  options: FetchClientUsersOptions
): Promise<ClientUser[] | null>
export function fetchClientUsers(options: FetchClientUsersOptions = {}): Promise<ClientUser[] | null> {
  const application = options.application === undefined ? 'webos' : options.application
  const params = new URLSearchParams()
  if (application) params.set('application', application)
  if (options.allClients) params.set('all_clients', 'true')
  const query = params.toString()
  return fetchUsers(
    `${env.backendApiUrl}/api/v1/users/me/client-users${query ? `?${query}` : ''}`,
    options.throwOnError
  )
}

/**
 * Admins assign across all clients via the admin-only assignment-users
 * endpoint; regular users only see their own client's users.
 */
export function fetchAssignableUsers(
  isAdmin: boolean,
  options?: { throwOnError?: false }
): Promise<ClientUser[]>
export function fetchAssignableUsers(
  isAdmin: boolean,
  options: { throwOnError?: boolean }
): Promise<ClientUser[] | null>
export function fetchAssignableUsers(
  isAdmin: boolean,
  options: { throwOnError?: boolean } = {}
): Promise<ClientUser[] | null> {
  if (!isAdmin) return fetchClientUsers({ throwOnError: options.throwOnError })
  return fetchUsers(
    `${env.backendApiUrl}/api/v1/users/assignment-users?application=webos`,
    options.throwOnError
  )
}

/**
 * Who may be made responsible for a document of this knowledge area (#631).
 *
 * Narrower than {@link fetchAssignableUsers} on purpose: the client's own users
 * who can actually open the area. Assigning someone who cannot open the
 * document produces a task nobody can do.
 */
export function fetchKnowledgeAreaAssignees(
  knowledgeAreaId: string,
  options: { throwOnError?: boolean } = {}
): Promise<ClientUser[] | null> {
  return fetchUsers(
    `${env.backendApiUrl}/api/v1/users/knowledge-area-assignees?knowledge_area_id=${encodeURIComponent(knowledgeAreaId)}`,
    options.throwOnError
  )
}
